import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
  writeBatch,
  runTransaction,
  Timestamp,
  DocumentData,
  setDoc,
  addDoc,
  deleteDoc,
  deleteField,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  User,
  CommercialAction,
  ActionGridRow,
  Country,
  Client,
  DropdownConfig,
  UserUpdateRequest,
  Invitation,
  // RowStatus,
} from '@/types';

// ============================================================================
// USER OPERATIONS
// ============================================================================

/**
 * Obtiene todos los usuarios pendientes de aprobación (sin rol)
 */
export async function getPendingUsers(): Promise<User[]> {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', null));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    })) as User[];
  } catch (error) {
    console.error('Error getting pending users:', error);
    throw error;
  }
}

/**
 * Actualiza los permisos de un usuario
 */
export async function updateUserPermissions(
  updateRequest: UserUpdateRequest
): Promise<void> {
  try {
    const userRef = doc(db, 'users', updateRequest.uid);

    // Para Manager, eliminar access_permissions (acceso completo)
    // Para otros roles, actualizar con los permisos especificados
    const updateData: any = {
      role: updateRequest.role,
      updatedAt: serverTimestamp(),
    };

    if (updateRequest.access_permissions !== undefined) {
      updateData.access_permissions = updateRequest.access_permissions;
    } else {
      // Manager: eliminar el campo access_permissions si existe
      const { deleteField } = await import('firebase/firestore');
      updateData.access_permissions = deleteField();
    }

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user permissions:', error);
    throw error;
  }
}

/**
 * Busca usuarios por email o nombre
 */
export async function searchUsers(searchTerm: string): Promise<User[]> {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users = snapshot.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    })) as User[];

    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(lowerSearch) ||
        user.displayName?.toLowerCase().includes(lowerSearch)
    );
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
}

/**
 * Obtiene los usuarios comerciales asignados a los países del manager
 * O los usuarios comerciales en las regiones del regional manager
 */
export async function getUsersByManager(
  managerCountries: string[],
  managerRegions?: string[]
): Promise<User[]> {
  try {
    if ((!managerCountries || managerCountries.length === 0) &&
      (!managerRegions || managerRegions.length === 0)) {
      return [];
    }

    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users = snapshot.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    })) as User[];

    return users.filter(user => {
      // Si es regional manager, filtrar por regiones
      if (managerRegions && managerRegions.length > 0) {
        // Solo mostrar usuarios comerciales (no managers ni regionales)
        if (user.role !== 'commercial') return false;

        // Verificar si el usuario tiene acceso a algún país en las regiones del manager
        if (!user.access_permissions?.countries) return false;

        // Necesitamos verificar si alguno de los países del usuario está en las regiones del manager
        // Esto requiere cargar la estructura, lo cual es costoso aquí
        // Por simplicidad, asumimos que el filtrado se hará en el componente
        // O podríamos pasar la estructura como parámetro
        return true; // Temporal, se refinará en el componente
      }

      // Si es manager normal, filtrar por países
      if (!user.access_permissions?.countries) return false;
      return user.access_permissions.countries.some(countryId =>
        managerCountries.includes(countryId)
      );
    });
  } catch (error) {
    console.error('Error getting users by manager:', error);
    throw error;
  }
}

/**
 * Obtiene TODOS los usuarios (para Managers)
 */
export async function getAllUsers(): Promise<User[]> {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    return snapshot.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    })) as User[];
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
}

/**
 * Elimina un usuario (Revoca acceso)
 */
export async function deleteUser(uid: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', uid);
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

// ============================================================================
// INVITATION OPERATIONS
// ============================================================================

/**
 * Crea una nueva invitación
 */
export async function createInvitation(invitationData: Partial<Invitation>): Promise<string> {
  try {
    const invitationsRef = collection(db, 'invitations');
    // Normalizar email a minúsculas para comparaciones seguras
    const email = invitationData.email?.toLowerCase();

    // Verificar si ya existe una invitación para este email
    const q = query(invitationsRef, where('email', '==', email));
    const existing = await getDocs(q);

    if (!existing.empty) {
      // Si existe, actualizamos la existente en lugar de duplicar
      const docId = existing.docs[0].id;
      await updateDoc(doc(db, 'invitations', docId), {
        ...invitationData,
        email, // asegurar minúsculas
        status: 'pending',
        updatedAt: serverTimestamp(),
      });
      return docId;
    }

    const docRef = await addDoc(invitationsRef, {
      ...invitationData,
      email, // asegurar minúsculas
      status: 'pending',
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
}

/**
 * Obtiene todas las invitaciones (para el AdminPanel)
 */
export async function getInvitations(): Promise<Invitation[]> {
  try {
    const invitationsRef = collection(db, 'invitations');
    const q = query(invitationsRef); // Puedes añadir orderBy si tienes índices
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Invitation[];
  } catch (error) {
    console.error('Error getting invitations:', error);
    throw error;
  }
}

/**
 * Borra una invitación
 */
export async function deleteInvitation(invitationId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'invitations', invitationId));
  } catch (error) {
    console.error('Error deleting invitation:', error);
    throw error;
  }
}

/**
 * Verifica si hay una invitación para un email
 */
export async function checkInvitation(email: string): Promise<Invitation | null> {
  try {
    if (!email) return null;
    const invitationsRef = collection(db, 'invitations');
    const q = query(invitationsRef, where('email', '==', email.toLowerCase()));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      return { id: docSnap.id, ...docSnap.data() } as Invitation;
    }
    return null;
  } catch (error) {
    console.error('Error checking invitation:', error);
    // No lanzar error para no bloquear el login, solo retornar null
    return null;
  }
}

// ============================================================================
// STRUCTURE OPERATIONS
// ============================================================================

/**
 * Obtiene la estructura de países y clientes
 */
export async function getStructure(): Promise<Record<string, Country>> {
  try {
    const structureRef = doc(db, 'structure', 'main');
    const structureDoc = await getDoc(structureRef);

    if (!structureDoc.exists()) {
      return {};
    }

    const data = structureDoc.data();
    // Transformar la estructura nueva a un formato plano de países para compatibilidad, 
    // o devolver la estructura completa si se necesita.
    // Por ahora, para mantener compatibilidad con el resto de la app que espera un mapa de países,
    // vamos a aplanar la estructura de regiones -> países.

    const countries: Record<string, Country> = {};
    if (data.regions) {
      Object.values(data.regions).forEach((region: any) => {
        if (region.countries) {
          // Object.assign(countries, region.countries);
          // Inyectamos el regionId para facilitar el filtrado
          Object.entries(region.countries).forEach(([cId, cData]: [string, any]) => {
            countries[cId] = {
              ...cData,
              regionId: region.id
            };
          });
        }
      });
    } else if (data.countries) {
      // Fallback para estructura antigua
      return data.countries;
    }

    return countries;
  } catch (error) {
    console.error('Error getting structure:', error);
    throw error;
  }
}

/**
 * Obtiene los clientes de un país específico
 */
export async function getClientsByCountry(
  countryId: string
): Promise<Record<string, Client>> {
  try {
    const structure = await getStructure();
    const country = structure[countryId];

    if (!country) {
      return {};
    }

    return country.clients || {};
  } catch (error) {
    console.error('Error getting clients by country:', error);
    throw error;
  }
}

/**
 * Helper para obtener el regionId de un país
 */
export async function getRegionIdForCountry(countryId: string): Promise<string | null> {
  try {
    const structureRef = doc(db, 'structure', 'main');
    const structureDoc = await getDoc(structureRef);

    if (!structureDoc.exists()) return null;

    const data = structureDoc.data();
    if (data.regions) {
      for (const [regionId, region] of Object.entries(data.regions)) {
        if ((region as any).countries && (region as any).countries[countryId]) {
          return regionId;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting region for country:', error);
    return null;
  }
}

/**
 * Verifica si un país ya existe en la estructura (por nombre)
 */
export async function checkCountryExists(countryName: string): Promise<boolean> {
  try {
    const structureRef = doc(db, 'structure', 'main');
    const structureDoc = await getDoc(structureRef);

    if (!structureDoc.exists()) return false;

    const data = structureDoc.data();
    if (data.regions) {
      for (const region of Object.values(data.regions)) {
        if ((region as any).countries && (region as any).countries[countryName]) {
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    console.error('Error checking country existence:', error);
    throw error;
  }
}

/**
 * Crea un nuevo país en la estructura y su documento de contador
 */
export async function createCountry(
  regionId: string,
  countryId: string, // This is the ISO Code (e.g. TN)
  countryName: string // This is the Full Name (e.g. Tunisia)
): Promise<void> {
  try {
    // 0. Safety Check: Verify if country already exists to avoid overwriting
    const exists = await checkCountryExists(countryName);
    if (exists) {
      throw new Error(`Country "${countryName}" already exists.`);
    }

    // 1. Actualizar estructura (structure/main)
    const structureRef = doc(db, 'structure', 'main');
    // Usamos el NOMBRE como key para coincidir con el resto del sistema
    const updatePath = `regions.${regionId}.countries.${countryName}`;

    await updateDoc(structureRef, {
      [updatePath]: {
        id: countryName,
        name: countryName,
        isoCode: countryId,
        clients: {} // Inicializar objeto de clientes vacío
      }
    });

    // 2. Crear documento del país (contador)
    // data/{regionId}/countries/{CountryName}
    // NOTA: El sistema usa el NOMBRE del país como ID del documento
    const countryRef = doc(db, 'data', regionId, 'countries', countryName);
    await setDoc(countryRef, {
      id: countryName,
      name: countryName,
      isoCode: countryId,
      lastRowNumber: 0
    });

  } catch (error) {
    console.error('Error creating country:', error);
    throw error;
  }
}

/**
 * Crea un nuevo cliente en la estructura y en la colección de clientes
 */
export async function createClient(countryName: string, clientData: Client): Promise<void> {
  try {
    const regionId = await getRegionIdForCountry(countryName);
    if (!regionId) throw new Error(`Region not found for country ${countryName}`);

    // 1. Actualizar estructura (structure/main)
    const structureRef = doc(db, 'structure', 'main');
    // Nota: Esto asume la estructura anidada regions -> regionId -> countries -> countryName -> clients
    // Usamos notación de puntos para actualizar solo el cliente específico
    const updatePath = `regions.${regionId}.countries.${countryName}.clients.${clientData.id}`;

    await updateDoc(structureRef, {
      [updatePath]: clientData
    });

    // 2. Crear documento del cliente (opcional, pero bueno para consistencia si se usa collectionGroup)
    // data/{regionId}/countries/{countryName}/clients/{clientId}
    const clientRef = doc(db, 'data', regionId, 'countries', countryName, 'clients', clientData.id);
    await setDoc(clientRef, {
      ...clientData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
}

/**
 * Renombrar un cliente y actualizar todas sus acciones
 */
export async function renameClient(
  countryId: string,
  clientId: string,
  newName: string
): Promise<void> {
  try {
    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) throw new Error(`Region not found for country ${countryId}`);

    // Usar writeBatch para atomicity (hasta 500 ops)
    // Si hay mucnas acciones, habría que paginar, pero asumimos volumen razonable por ahora
    const batch = writeBatch(db);

    // 1. Actualizar estructura (structure/main)
    const structureRef = doc(db, 'structure', 'main');
    const updatePath = `regions.${regionId}.countries.${countryId}.clients.${clientId}.name`;
    batch.update(structureRef, { [updatePath]: newName });

    // 2. Actualizar documento del cliente
    // data/{regionId}/countries/{countryId}/clients/{clientId}
    const clientRef = doc(db, 'data', regionId, 'countries', countryId, 'clients', clientId);
    batch.update(clientRef, {
      name: newName,
      updatedAt: serverTimestamp()
    });

    // 3. Obtener todas las acciones del cliente
    const actionsRef = collection(
      db,
      'data',
      regionId,
      'countries',
      countryId,
      'clients',
      clientId,
      'actions'
    );
    const actionsSnapshot = await getDocs(actionsRef);

    // 4. Actualizar cada acción
    actionsSnapshot.docs.forEach((doc) => {
      // Solo actualizamos si el nombre es diferente (optimization) o forzamos sync
      batch.update(doc.ref, {
        client: newName,
        syncedToSAP: false, // Forzar resincronización
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

  } catch (error) {
    console.error('Error renaming client:', error);
    throw error;
  }
}

/**
 * Corrige el código SAP (id) de un cliente ya existente por el código SAP correcto.
 *
 * - Si `newClientId` no existe todavía como cliente en ese país, se crea (con el mismo
 *   nombre/plant del cliente antiguo, o el nombre indicado en `options.newName`).
 * - Si `newClientId` ya existe (caso de fusión con un cliente duplicado), se reutiliza ese
 *   cliente existente y, si se indica `options.newName`, se renombra vía `renameClient`.
 * - Mueve todas las acciones ACTIVAS (rowStatus !== 'Eliminated') del cliente antiguo al
 *   nuevo/destino, reutilizando `moveAction` (crea la acción en destino + soft-delete en origen).
 * - Retira la entrada del cliente antiguo de `structure/main` y borra su documento en `data/`.
 *
 * Solo opera sobre Commercial Excellence (`collectionRoot: 'data'`); no toca Mitigation.
 */
export async function updateClientSapCode(
  countryId: string,
  oldClientId: string,
  newClientId: string,
  options?: { newName?: string }
): Promise<{ movedActions: number; merged: boolean }> {
  try {
    if (oldClientId === newClientId) {
      throw new Error('oldClientId y newClientId no pueden ser iguales');
    }

    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) throw new Error(`Region not found for country ${countryId}`);

    const structureRef = doc(db, 'structure', 'main');
    const structureSnap = await getDoc(structureRef);
    if (!structureSnap.exists()) throw new Error('structure/main not found');

    const structureData = structureSnap.data();
    const countryClients: Record<string, Client> =
      structureData?.regions?.[regionId]?.countries?.[countryId]?.clients || {};

    const oldClient = countryClients[oldClientId];
    if (!oldClient) throw new Error(`Client ${oldClientId} not found under ${countryId}`);

    const destExists = !!countryClients[newClientId];

    if (!destExists) {
      // Crear cliente nuevo con el código correcto, conservando nombre/plant (o el nuevo nombre)
      await createClient(countryId, {
        id: newClientId,
        name: options?.newName || oldClient.name,
        plant: oldClient.plant || '',
      });
    } else if (options?.newName) {
      // Fusión con cliente ya existente: renombrar si se pide
      await renameClient(countryId, newClientId, options.newName);
    }

    const finalName =
      options?.newName || (destExists ? countryClients[newClientId].name : oldClient.name);

    // Mover todas las acciones activas del cliente antiguo al nuevo/destino
    const oldActionsRef = collection(
      db, 'data', regionId, 'countries', countryId, 'clients', oldClientId, 'actions'
    );
    const oldActionsSnap = await getDocs(oldActionsRef);

    let movedActions = 0;
    for (const actionDoc of oldActionsSnap.docs) {
      const actionData = actionDoc.data();
      if (actionData.rowStatus === 'Eliminated') continue; // no migrar lo ya eliminado

      await moveAction(
        countryId,
        oldClientId,
        newClientId,
        { ...actionData, sapCode: newClientId, client: finalName },
        actionDoc.id,
        'data'
      );
      movedActions++;
    }

    // Retirar cliente antiguo de structure/main y de data/
    const oldClientPath = `regions.${regionId}.countries.${countryId}.clients.${oldClientId}`;
    await updateDoc(structureRef, { [oldClientPath]: deleteField() });

    const oldClientRef = doc(db, 'data', regionId, 'countries', countryId, 'clients', oldClientId);
    await deleteDoc(oldClientRef);

    return { movedActions, merged: destExists };
  } catch (error) {
    console.error('Error updating client SAP code:', error);
    throw error;
  }
}

// ============================================================================
// ACTIONS OPERATIONS
// ============================================================================

/**
 * Convierte Timestamp a Date para el grid
 */
function timestampToDate(timestamp: any): Date | null {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
}

/**
 * Convierte datos de Firestore a formato del grid
 */
function convertToGridRow(doc: DocumentData): ActionGridRow {
  return {
    id: doc.id,
    rowNumber: doc.rowNumber || 0,
    sapCode: doc.sapCode || null,
    client: doc.client || '',
    plant: doc.plant || '',
    dateIntroduced: timestampToDate(doc.dateIntroduced),
    dateFinalized: timestampToDate(doc.dateFinalized),
    typeOfAction: doc.typeOfAction || '',
    vcpConcept: doc.vcpConcept || '',
    volumeYearTn: doc.volumeYearTn || 0,
    gmIIEurKg: doc.gmIIEurKg || 0,
    cost: doc.cost || 0,
    annualExpectedImpact: doc.annualExpectedImpact || 0,
    achievedAnnualImpact: doc.achievedAnnualImpact || null,
    timing: doc.timing || '',
    feasibility: doc.feasibility || 'Medium',
    status: doc.status || 'Not Started',
    picOwner: doc.picOwner || '',
    country: doc.country || '',
    brand: doc.brand || '',
    familyRep: doc.familyRep || '',
    codSapMaterial: doc.codSapMaterial || '',
    notes: doc.notes || '',
    rowStatus: doc.rowStatus || null, // Mapeamos el nuevo campo
  };
}

/**
 * Obtiene las acciones de un cliente específico
 * FILTRA LAS QUE TENGAN STATUS 'Eliminated' SALVO QUE SE PIDA INCLUIRLAS
 */
export async function getActionsByClient(
  countryId: string,
  clientId: string,
  includeDeleted: boolean = false,
  collectionRoot: string = 'data'
): Promise<ActionGridRow[]> {
  try {
    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) {
      console.error(`Region not found for country ${countryId}`);
      return [];
    }

    const actionsRef = collection(
      db,
      collectionRoot,
      regionId,
      'countries',
      countryId,
      'clients',
      clientId,
      'actions'
    );

    // Obtener todas y filtrar en cliente para simplificar índices
    // Idealmente usaríamos un query(where...), pero requiere índices compuestos si ordenamos
    const snapshot = await getDocs(actionsRef);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data();
        return convertToGridRow({ id: doc.id, ...data });
      })
      .filter((row) => includeDeleted ? true : row.rowStatus !== 'Eliminated'); // SOFT DELETE FILTER

  } catch (error) {
    console.error('Error getting actions by client:', error);
    throw error;
  }
}

/**
 * Obtiene todas las acciones de todos los clientes de un país
 * FILTRA LAS 'Eliminated' SALVO QUE SE PIDA INCLUIRLAS
 */
export async function getActionsByCountry(
  countryId: string,
  includeDeleted: boolean = false,
  collectionRoot: string = 'data'
): Promise<ActionGridRow[]> {
  try {
    // 1. Obtener clientes del país
    const clients = await getClientsByCountry(countryId);

    // 2. Obtener acciones de todos los clientes en paralelo
    const promises = Object.keys(clients).map(async (clientId) => {
      const clientName = clients[clientId].name;
      const actions = await getActionsByClient(countryId, clientId, includeDeleted, collectionRoot);

      // 3. Inyectar clientId y clientName en cada acción
      return actions.map(action => ({
        ...action,
        clientId: clientId, // Importante para guardar luego
        client: clientName, // Para mostrar en el grid
      }));
    });

    const results = await Promise.all(promises);

    // 4. Aplanar el array
    return results.flat();

  } catch (error) {
    console.error('Error getting actions by country:', error);
    throw error;
  }
}

/**
 * Obtiene una acción específica
 */
export async function getActionById(
  countryId: string,
  clientId: string,
  actionId: string,
  collectionRoot: string = 'data'
): Promise<CommercialAction | null> {
  try {
    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) return null;

    const actionRef = doc(
      db,
      collectionRoot,
      regionId,
      'countries',
      countryId,
      'clients',
      clientId,
      'actions',
      actionId
    );

    const actionDoc = await getDoc(actionRef);

    if (!actionDoc.exists()) {
      return null;
    }

    return {
      id: actionDoc.id,
      ...actionDoc.data(),
    } as CommercialAction;
  } catch (error) {
    console.error('Error getting action by id:', error);
    throw error;
  }
}

/**
 * Crea una nueva acción comercial
 * ESTABLECE rowStatus = 'Created'
 */
export async function createAction(
  countryId: string,
  clientId: string,
  actionData: Partial<CommercialAction>,
  collectionRoot: string = 'data'
): Promise<string> {
  try {
    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) throw new Error(`Region not found for country ${countryId}`);

    // Usar transacción para asegurar rowNumber único y consecutivo por país
    return await runTransaction(db, async (transaction) => {
      // 1. Referencia al documento del país (contador)
      const countryRef = doc(db, collectionRoot, regionId, 'countries', countryId);
      const countryDoc = await transaction.get(countryRef);

      // 2. Calcular siguiente número
      let nextRowNumber = 1;
      if (countryDoc.exists()) {
        const data = countryDoc.data();
        nextRowNumber = (data.lastRowNumber || 0) + 1;
      }

      // 3. Referencia para la nueva acción
      const actionsRef = collection(
        db,
        collectionRoot,
        regionId,
        'countries',
        countryId,
        'clients',
        clientId,
        'actions'
      );
      const newActionRef = doc(actionsRef);

      // 4. Crear la acción
      transaction.set(newActionRef, {
        ...actionData,
        rowNumber: nextRowNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        syncedToSAP: false,
        rowStatus: 'Created', // STATUS INICIAL
      });

      // 5. Actualizar el contador
      transaction.set(countryRef, { lastRowNumber: nextRowNumber }, { merge: true });

      return newActionRef.id;
    });
  } catch (error) {
    console.error('Error creating action:', error);
    throw error;
  }
}

/**
 * Elimina múltiples acciones (SOFT DELETE)
 * Marca como 'Eliminated' y syncedToSAP = false
 */
export async function deleteActions(
  countryId: string,
  clientId: string,
  actionIds: string[],
  collectionRoot: string = 'data'
): Promise<void> {
  try {
    const regionId = await getRegionIdForCountry(countryId);
    if (!regionId) throw new Error(`Region not found for country ${countryId}`);

    const batch = writeBatch(db);

    actionIds.forEach((actionId) => {
      const actionRef = doc(
        db,
        collectionRoot,
        regionId,
        'countries',
        countryId,
        'clients',
        clientId,
        'actions',
        actionId
      );

      // SOFT DELETE: Actualizamos en lugar de borrar
      batch.update(actionRef, {
        rowStatus: 'Eliminated',
        syncedToSAP: false,
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error deleting actions:', error);
    throw error;
  }
}

/**
 * Mueve una acción de un cliente a otro
 * Crea una nueva en el destino y borra la original
 */
export async function moveAction(
  countryId: string,
  originalClientId: string,
  newClientId: string,
  actionData: Partial<CommercialAction>,
  originalActionId: string,
  collectionRoot: string = 'data'
): Promise<string> {
  try {
    // 1. Crear la acción en el nuevo cliente
    // Preservamos la mayoría de datos, pero createAction asignará nuevo rowNumber
    // y timestamps de create/update si no se sobrescriben.
    // Pasamos los datos actualizados.
    const newActionId = await createAction(countryId, newClientId, actionData, collectionRoot);

    // 2. Borrar la acción del cliente original (Soft Delete)
    await deleteActions(countryId, originalClientId, [originalActionId], collectionRoot);

    return newActionId;
  } catch (error) {
    console.error('Error moving action:', error);
    throw error;
  }
}

// ============================================================================
// CONFIG OPERATIONS
// ============================================================================

/**
 * Obtiene la configuración de dropdowns
 */
export async function getDropdownConfig(): Promise<DropdownConfig> {
  try {
    const configRef = doc(db, 'config', 'dropdowns');
    const configDoc = await getDoc(configRef);

    if (!configDoc.exists()) {
      // Retornar valores por defecto
      return {
        timing: ['2025 Q1', '2025 Q2', '2025 Q3', '2025 Q4', '2026 Q1'],
        feasibility: ['High', 'Medium', 'Low'],
        status: ['CS', 'CNS', 'WCTD', 'WIP', 'Not Started'],
        typeOfAction: ['Cross selling', 'Volume growth', 'Pricing', 'Diversification'],
        vcp: {},
        brand: [],
        familyRep: [],
      };
    }

    const data = configDoc.data();
    return {
      timing: data.timing || [],
      feasibility: data.feasibility || [],
      status: data.status || [],
      typeOfAction: data.typeOfAction || [],
      vcp: data.vcp || data.VCP || {}, // Handle both cases
      brand: data.brand || [],
      familyRep: data.familiaReporting || [], // Map from familiaReporting to familyRep
    };
  } catch (error) {
    console.error('Error getting dropdown config:', error);
    throw error;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Verifica si un usuario tiene acceso a un país
 */
export function hasCountryAccess(user: User, countryId: string): boolean {
  // Managers tienen acceso a todos los países
  if (user.role === 'manager') return true;
  if (user.role === 'innovation') return true;
  if (user.role === 'innovation++') return true;

  if (!user.access_permissions) return false;

  // Regional users tienen acceso a países de sus regiones (verificado en otro nivel)
  if (user.role === 'regional' && user.access_permissions.regions) return true;

  // Commercial users solo tienen acceso a países específicos
  if (!user.access_permissions.countries) return false;
  if (!Array.isArray(user.access_permissions.countries)) return false;

  return user.access_permissions.countries.includes(countryId);
}

/**
 * Verifica si un usuario tiene acceso a un cliente
 */
export function hasClientAccess(
  user: User,
  countryId: string,
  clientId: string
): boolean {
  if (user.role === 'manager') return true;
  if (user.role === 'innovation') return true;
  if (user.role === 'innovation++') return true;
  if (!user.access_permissions) return false;

  // Verificar acceso al país
  if (!hasCountryAccess(user, countryId)) return false;

  // Si clients es null o undefined, tiene acceso a todos los clientes del país
  if (user.access_permissions.clients === null || user.access_permissions.clients === undefined) return true;

  // Verificar si el cliente está en la lista
  return user.access_permissions.clients.includes(clientId);
}

/**
 * Filtra países según permisos del usuario
 */
export function filterCountriesByPermissions(
  countries: Record<string, Country>,
  user: User
): Record<string, Country> {
  // Si es Manager, mostrar TODOS los países (sin restricciones)
  if (user.role === 'manager' || user.role === 'innovation' || user.role === 'innovation++') {
    return countries;
  }

  // Para otros roles, verificar access_permissions
  if (!user.access_permissions) return {};

  // Si es Regional, mostrar países de sus regiones
  if (user.role === 'regional' && user.access_permissions.regions) {
    const allowedCountries: Record<string, Country> = {};
    const userRegions = user.access_permissions.regions;

    Object.values(countries).forEach((country) => {
      // Verificamos si el país pertenece a una de las regiones permitidas
      if (country.regionId && userRegions.includes(country.regionId)) {
        allowedCountries[country.id] = country;
      }
    });

    return allowedCountries;
  }

  // Para Commercial, filtrar por países asignados
  if (!user.access_permissions.countries) return {};

  const allowedCountries: Record<string, Country> = {};

  // Asegurar que countries es un array
  if (Array.isArray(user.access_permissions.countries)) {
    user.access_permissions.countries.forEach((countryId) => {
      if (countries[countryId]) {
        allowedCountries[countryId] = countries[countryId];
      }
    });
  }

  return allowedCountries;
}

/**
 * Filtra clientes según permisos del usuario
 */
export function filterClientsByPermissions(
  clients: Record<string, Client>,
  user: User,
  countryId: string
): Record<string, Client> {
  // Si es Manager, retornar todos los clientes (acceso completo)
  if (user.role === 'manager' || user.role === 'innovation' || user.role === 'innovation++') {
    return clients;
  }

  // Para otros roles, verificar access_permissions
  if (!user.access_permissions) return {};

  // Si no tiene acceso al país, retornar vacío
  if (!hasCountryAccess(user, countryId)) return {};

  // Si clients es null o undefined, retornar todos los clientes del país
  if (user.access_permissions.clients === null || user.access_permissions.clients === undefined) {
    return clients;
  }

  // Si clients es un array, filtrar por los IDs permitidos
  const allowedClients: Record<string, Client> = {};

  if (Array.isArray(user.access_permissions.clients)) {
    user.access_permissions.clients.forEach((clientId) => {
      if (clients[clientId]) {
        allowedClients[clientId] = clients[clientId];
      }
    });
  }

  return allowedClients;
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const firestoreService = {
  // Users
  getPendingUsers,
  updateUserPermissions,
  searchUsers,
  getUsersByManager,
  getAllUsers,

  // Invitations
  createInvitation,
  getInvitations,
  deleteInvitation,
  checkInvitation,

  // Structure
  getStructure,
  getClientsByCountry,
  createClient,

  // Actions
  getActionsByClient,
  getActionById,
  createAction,
  deleteActions,

  // Config
  getDropdownConfig,

  // Helpers
  hasCountryAccess,
  hasClientAccess,
  filterCountriesByPermissions,
  filterClientsByPermissions,
};

export default firestoreService;
